import { useEffect, useRef, useState, type RefObject } from 'react';
import { CheckIconV2, CloseIcon, Tooltip, TOOLTIP_SURFACE } from '@repo/atomic-components';
import { MicrophoneOutlineIcon } from './MicrophoneOutlineIcon';
import { HeaderIconButton } from '@/ui/pages/ChatPage/components/HeaderIconButton';

/** Wavy strand paths for the dictation waveform, each with its own glow and sway. */
const WAVEFORM_WIDTH_PX = 72;
const WAVEFORM_HEIGHT_PX = 24;

// Explicit `Q` segments so each wave's height can differ (taller in the middle). Every strand
// starts and ends at y=12, and strands come in mirrored pairs.
const SPAGHETTI_STRANDS = [
  // Low-frequency mirror pair — 3 segments, amps 3 / 9 / 3.
  {
    key: 'a',
    colour: '#fd6100',
    width: 2.6,
    opacity: 0.95,
    durationMs: 1700,
    delayMs: 0,
    direction: 'normal',
    keyframe: 'dictation-spaghetti-sway-a',
    d: 'M -2 12 Q 12 9, 26 12 Q 40 21, 54 12 Q 68 15, 74 12',
  },
  {
    key: 'b',
    colour: '#ff00fb',
    width: 2.2,
    opacity: 0.85,
    durationMs: 1250,
    delayMs: 220,
    direction: 'reverse',
    keyframe: 'dictation-spaghetti-sway-c',
    d: 'M -2 12 Q 12 15, 26 12 Q 40 3, 54 12 Q 68 9, 74 12',
  },
  // Medium-frequency mirror pair — 4 segments, amps 2 / 7 / 7 / 2.
  {
    key: 'c',
    colour: '#7b00ff',
    width: 2.4,
    opacity: 0.9,
    durationMs: 2050,
    delayMs: 460,
    direction: 'alternate',
    keyframe: 'dictation-spaghetti-sway-b',
    d: 'M -2 12 Q 8 10, 18 12 Q 28 5, 38 12 Q 48 19, 58 12 Q 68 14, 74 12',
  },
  {
    key: 'd',
    colour: '#00cadb',
    width: 1.8,
    opacity: 0.8,
    durationMs: 1400,
    delayMs: 90,
    direction: 'normal',
    keyframe: 'dictation-spaghetti-sway-d',
    d: 'M -2 12 Q 8 14, 18 12 Q 28 19, 38 12 Q 48 5, 58 12 Q 68 10, 74 12',
  },
  // High-frequency mirror pair — 6 segments, amps 2 / 3 / 6 / 6 / 3 / 2.
  {
    key: 'e',
    colour: '#ff5da2',
    width: 1.6,
    opacity: 0.8,
    durationMs: 1050,
    delayMs: 340,
    direction: 'reverse',
    keyframe: 'dictation-spaghetti-sway-a',
    d: 'M -2 12 Q 3 10, 9 12 Q 15 15, 22 12 Q 29 6, 36 12 Q 43 18, 50 12 Q 57 9, 63 12 Q 69 14, 74 12',
  },
  {
    key: 'f',
    colour: '#17a398',
    width: 1.6,
    opacity: 0.7,
    durationMs: 1900,
    delayMs: 130,
    direction: 'alternate-reverse',
    keyframe: 'dictation-spaghetti-sway-d',
    d: 'M -2 12 Q 3 14, 9 12 Q 15 9, 22 12 Q 29 18, 36 12 Q 43 6, 50 12 Q 57 15, 63 12 Q 69 10, 74 12',
  },
  // White strand, tallest in the middle.
  {
    key: 'g',
    colour: '#ffffff',
    width: 1.4,
    opacity: 0.7,
    durationMs: 2200,
    delayMs: 610,
    direction: 'normal',
    keyframe: 'dictation-spaghetti-sway-c',
    d: 'M -2 12 Q 8 11, 18 12 Q 28 6, 38 12 Q 48 20, 58 12 Q 68 13, 74 12',
  },
] as const;

/** m:ss timer. Mounted once actually listening, so it counts only real recording time. */
const DictationTimer = () => {
  const [elapsedMs, setElapsedMs] = useState(0);
  useEffect(() => {
    const startedAt = performance.now();
    const interval = window.setInterval(() => {
      setElapsedMs(performance.now() - startedAt);
    }, 250);
    return () => window.clearInterval(interval);
  }, []);
  const totalSeconds = Math.floor(elapsedMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return (
    <span className="text-theme-black text-[0.8125rem] font-semibold tabular-nums">
      {minutes}:{seconds.toString().padStart(2, '0')}
    </span>
  );
};

export interface DictationControlProps {
  /** Whether the browser/polyfill supports SpeechRecognition. When false, nothing renders. */
  isSupported: boolean;
  /** True once the recogniser has actually started emitting — enables the timer and animates the soundwave. */
  isListening: boolean;
  /** True while the recogniser is tearing down after Done — shows the mic in a busy state. */
  isBusy: boolean;
  /** Idle-state button is disabled (e.g. the surrounding input isn't ready yet). */
  disabled?: boolean;
  /** Set as soon as the mic is clicked, before the recogniser is listening, so the UI responds immediately. */
  isStarting: boolean;
  onStart: () => void;
  onCancel: () => void;
  onConfirm: () => void;
  /** Ref forwarded onto the idle-state mic button for focus management. */
  startButtonRef?: RefObject<HTMLButtonElement | null>;
}

/**
 * Dictation control: a mic button when idle, a pill with waveform, timer and done/cancel while
 * listening. The pill shows right after the click with a static waveform and `0:00`; the timer and
 * animation start once actually listening.
 */
export const DictationControl = ({
  isSupported,
  isListening,
  isBusy,
  disabled,
  isStarting,
  onStart,
  onCancel,
  onConfirm,
  startButtonRef,
}: DictationControlProps) => {
  const cancelBtnRef = useRef<HTMLButtonElement>(null);
  const confirmBtnRef = useRef<HTMLButtonElement>(null);

  if (!isSupported) return null;

  const showPill = isListening || isStarting;

  if (showPill) {
    return (
      <div
        role="status"
        aria-live="polite"
        aria-label={isListening ? 'Dictation in progress' : 'Starting dictation'}
        className="border-theme-border-grey/50 upload-chip-shadow relative flex h-8 origin-right items-center gap-3 overflow-hidden rounded-md border pr-1 pl-2.5 backdrop-blur-xl backdrop-saturate-[1.4]"
        style={{
          backgroundColor: 'rgba(26, 29, 41, 0.005)',
          animation: 'dictation-listening-in 260ms cubic-bezier(0.16, 1, 0.3, 1) both',
        }}
      >
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              'radial-gradient(ellipse 60% 140% at 50% 50%, rgba(255, 93, 162, 0.08) 0%, rgba(123, 0, 255, 0.06) 35%, rgba(0, 202, 219, 0.03) 65%, transparent 90%)',
            animation: 'dictation-pill-gradient-drift 4600ms ease-in-out infinite',
            animationPlayState: isListening ? 'running' : 'paused',
            willChange: 'transform, opacity',
          }}
        />
        <div className="relative z-10 flex shrink-0 items-center gap-1.5">
          <span
            aria-hidden
            className="block h-2 w-2 shrink-0 rounded-full bg-red-500"
            style={{
              animation: 'dictation-recording-dot 1200ms ease-in-out infinite',
              animationPlayState: isListening ? 'running' : 'paused',
            }}
          />
          {/* The real timer starts only while listening; `0:00` placeholder until then keeps the width stable. */}
          {isListening ? (
            <DictationTimer />
          ) : (
            <span className="text-theme-black text-[0.8125rem] font-semibold tabular-nums">0:00</span>
          )}
        </div>
        <svg
          aria-hidden
          width={WAVEFORM_WIDTH_PX}
          height={WAVEFORM_HEIGHT_PX}
          viewBox={`0 0 ${WAVEFORM_WIDTH_PX} ${WAVEFORM_HEIGHT_PX}`}
          fill="none"
          className="text-theme-text-grey relative z-10 shrink-0"
          style={{
            isolation: 'isolate',
            maskImage: 'linear-gradient(90deg, transparent 0, black 8px, black calc(100% - 8px), transparent 100%)',
            WebkitMaskImage:
              'linear-gradient(90deg, transparent 0, black 8px, black calc(100% - 8px), transparent 100%)',
          }}
        >
          <path
            d={`M 0 ${WAVEFORM_HEIGHT_PX / 2} L ${WAVEFORM_WIDTH_PX} ${WAVEFORM_HEIGHT_PX / 2}`}
            stroke="currentColor"
            strokeWidth={1.5}
            strokeLinecap="round"
            style={{
              opacity: isListening ? 0 : 1,
              transition: 'opacity 260ms ease-out',
            }}
          />
          <g
            style={{
              opacity: isListening ? 1 : 0,
              transform: isListening ? 'scaleY(1)' : 'scaleY(0)',
              transformOrigin: 'center',
              transformBox: 'fill-box',
              transition: 'opacity 320ms ease-in 60ms, transform 420ms cubic-bezier(0.16, 1, 0.3, 1) 60ms',
            }}
          >
            {SPAGHETTI_STRANDS.map((strand) => (
              <path
                key={strand.key}
                className="dictation-spaghetti-strand"
                d={strand.d}
                stroke={strand.colour}
                strokeWidth={strand.width}
                strokeLinecap="round"
                strokeLinejoin="round"
                opacity={strand.opacity}
                style={{
                  mixBlendMode: 'plus-lighter',
                  filter: `drop-shadow(0 0 1.5px ${strand.colour})`,
                  animationName: strand.keyframe,
                  animationDuration: `${strand.durationMs}ms`,
                  animationDelay: `${strand.delayMs}ms`,
                  animationDirection: strand.direction,
                  animationPlayState: isListening ? 'running' : 'paused',
                }}
              />
            ))}
          </g>
        </svg>
        <div className="relative z-10 flex shrink-0 items-center gap-1">
          <button
            ref={cancelBtnRef}
            type="button"
            onClick={onCancel}
            aria-label="Cancel dictation"
            className="text-theme-text-grey hover:bg-theme-border-grey/70 hover:text-theme-black flex h-6 w-6 shrink-0 cursor-pointer items-center justify-center rounded-md border-none bg-transparent p-0 transition-colors focus:outline-none"
          >
            <CloseIcon width={10} height={10} />
          </button>
          <Tooltip
            target={cancelBtnRef as RefObject<HTMLElement>}
            content="Cancel dictation"
            position="top"
            className={TOOLTIP_SURFACE}
            baseZIndex={10000}
          />
          <button
            ref={confirmBtnRef}
            type="button"
            onClick={onConfirm}
            // Disabled until actually listening, or it would commit an empty transcript.
            disabled={!isListening}
            aria-label="Insert dictated text"
            className="text-theme-text-grey hover:bg-theme-border-grey/70 hover:text-theme-black disabled:hover:text-theme-text-grey flex h-6 w-6 shrink-0 cursor-pointer items-center justify-center rounded-md border-none bg-transparent p-0 transition-colors focus:outline-none disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
          >
            <CheckIconV2 width={12} height={10} />
          </button>
          <Tooltip
            target={confirmBtnRef as RefObject<HTMLElement>}
            content="Insert dictated text"
            position="top"
            className={TOOLTIP_SURFACE}
            baseZIndex={10000}
          />
        </div>
      </div>
    );
  }

  return (
    <HeaderIconButton
      ref={startButtonRef}
      icon={<MicrophoneOutlineIcon width={18} height={18} />}
      tooltip="Dictate"
      onClick={onStart}
      disabled={disabled || isBusy}
    />
  );
};
